import { FC, KeyboardEvent, ReactNode, useEffect, useState } from "react";
import {
  alpha,
  Box,
  ButtonBase,
  ClickAwayListener,
  InputBase,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import { observer } from "mobx-react-lite";

import { useClaimsStore } from "../../../hooks/useClaimsStore";
import { useOrgLinkBranding } from "../../../contexts/AuthContext";
import { ClaimsIcon } from "../../icons";
import { PreDefaultQuestionType } from "../../../types/claims";
import {
  GradientIconButton,
  violetGradient,
  violetGradientHover,
} from "../../ui/core/GradientButton";

interface AskReveActionMenuProps {
  disabled?: boolean;
  /** Fires with the prompt to send (and optional agent override) when the free-text input is submitted */
  onSelectAction: (question: string, agentName?: string) => void;
  /** Fires when an ActionRow (predefault question) is clicked, instead of sending immediately */
  onSelectPredefaultQuestion: (question: PreDefaultQuestionType) => void;
}

const AnalyzeClaimIcon: FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M8.667 1.333H4a1.333 1.333 0 0 0-1.333 1.334v10.666A1.333 1.333 0 0 0 4 14.667h5.333"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8.667 1.333v3.334h3.333"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle
      cx="9.667"
      cy="10.667"
      r="2.333"
      stroke="currentColor"
      strokeWidth="1.2"
    />
    <path
      d="M11.5 12.5 13 14"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
    />
  </svg>
);

const SendArrowIcon: FC = () => {
  const theme = useTheme();
  return (
    <svg width="16" height="16" viewBox="0 0 14 14" fill="none">
      <path
        d="M2.917 7h8.166M7.583 3.5 11.083 7l-3.5 3.5"
        stroke={theme.palette.common.white}
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const ChevronDownIcon: FC = () => {
  const theme = useTheme();
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path
        d="M2.5 4.25 6 7.75l3.5-3.5"
        stroke={theme.palette.primary.contrastText}
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

interface ActionRowProps {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  onClick: () => void;
}

const ActionRow: FC<ActionRowProps> = ({ icon, title, subtitle, onClick }) => (
  <ButtonBase
    onClick={onClick}
    sx={{
      width: "100%",
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "flex-start",
      gap: 1.25,
      borderRadius: 2,
      p: 1,
      textAlign: "left",
      "&:hover": { backgroundColor: "custom.violetLight" },
      background: "transparent",
    }}
  >
    <Box
      sx={{
        display: "flex",
        flexShrink: 0,
        height: 32,
        width: 32,
        alignItems: "center",
        justifyContent: "center",
        color: "custom.violetMain",
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary" }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
        {subtitle}
      </Typography>
    </Box>
  </ButtonBase>
);

const AskReveActionMenu: FC<AskReveActionMenuProps> = observer(({
  disabled,
  onSelectAction,
  onSelectPredefaultQuestion,
}) => {
  const theme = useTheme();
  const { predefaultQuestions } = useClaimsStore();
  const { askReveLabel } = useOrgLinkBranding();
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState("");

  const actionMeta: Record<string, { icon: ReactNode; subtitle: string }> = {
    claim_reviewer: {
      icon: <AnalyzeClaimIcon />,
      subtitle: "Find denial patterns, suggest next steps.",
    },
    appeal_strategy: {
      icon: <ClaimsIcon fill={theme.palette.custom.violetMain} />,
      subtitle: "Letter or strategy. Checks your docs first.",
    },
    dofr_appeal: {
      icon: <ClaimsIcon fill={theme.palette.custom.violetMain} />,
      subtitle: "File a DOFR appeal for this claim.",
    },
  };
  const defaultActionMeta = {
    icon: <ClaimsIcon fill={theme.palette.custom.violetMain} />,
    subtitle: "Quick action for this claim.",
  };

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const handleAskReveClick = () => {
    setOpen(false);
    setQuestion("");
    onSelectPredefaultQuestion({ question: "", isWelcome: true });
  };

  const handleToggle = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    
    if (disabled) return;
    setOpen((prev) => !prev);
  };

  const handlePredefaultQuestionClick = (predefaultQuestion: PreDefaultQuestionType) => {
    setOpen(false);
    setQuestion("");
    onSelectPredefaultQuestion(predefaultQuestion);
  };

  const handleSend = () => {
    const trimmed = question.trim();
    if (!trimmed) return;
    setOpen(false);
    setQuestion("");
    onSelectAction(trimmed, undefined);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box sx={{ position: "relative", display: "flex", width: "100%", maxWidth: 350, pointerEvents: "none" }}>
        {open && (
          <Paper
            elevation={8}
            sx={{
              position: "absolute",
              bottom: "100%",
              left: 0,
              mb: 1.5,
              minWidth: "100%",
              pointerEvents: "auto",
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              p: 1,
            }}
          >
            <Typography
              sx={{
                px: 1,
                pt: 0.5,
                pb: 1,
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.05em",
                color: "text.disabled",
              }}
            >
              ACTIONS
            </Typography>

            {predefaultQuestions.map((predefaultQuestion) => {
              const meta =
                (predefaultQuestion.agent_name &&
                  actionMeta[predefaultQuestion.agent_name]) ||
                defaultActionMeta;
              return (
                <ActionRow
                  key={predefaultQuestion.question}
                  icon={meta.icon}
                  title={predefaultQuestion.question}
                  subtitle={meta.subtitle}
                  onClick={() =>
                    handlePredefaultQuestionClick(predefaultQuestion)
                  }
                />
              );
            })}

            <Box
              sx={{
                mt: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
                borderRadius: 999,
                backgroundColor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                pl: 2,
                pr: 0.5,
                py: 0.5,
              }}
            >
              <InputBase
                autoFocus
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Or ask anything about this claim..."
                sx={{ flex: 1, minWidth: 0, fontSize: 13 }}
              />
              <GradientIconButton
                onClick={handleSend}
                disabled={!question.trim()}
                aria-label="Send"
                sx={{ height: 36, width: 36 }}
              >
                <SendArrowIcon />
              </GradientIconButton>
            </Box>
          </Paper>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "stretch",
            pointerEvents: "auto",
            boxShadow: disabled ? "none" : 4,
            borderRadius: 999,
            overflow: "hidden",
            background: open ? theme.palette.custom.cardDark : violetGradient(theme),
            ...(!open &&
              !disabled && {
                "&:hover": {
                  background: violetGradientHover(theme),
                },
              }),
          }}
        >
          <ButtonBase
            onClick={handleAskReveClick}
            disabled={disabled}
            sx={{
              display: "flex",
              alignItems: "center",
              // The label is org-configurable (REV-2371): keep the stock
              // "Ask Reve" footprint as a floor, let longer labels grow, and
              // truncate rather than overflow into the chevron.
              minWidth: "110px",
              maxWidth: 220,
              whiteSpace: "nowrap",
              gap: 0.5,
              color: "primary.contrastText",
              pl: 2,
              pr: 0.75,
              py: 1.2,
              "&:active": {
                backgroundColor: alpha(theme.palette.common.black, 0.1),
              },
              "&.Mui-disabled": { opacity: 0.5 },
            }}
          >
            <Typography
              noWrap
              sx={{ fontSize: 13, fontWeight: 500, color: "inherit" }}
            >
              {askReveLabel}
            </Typography>
          </ButtonBase>

          <ButtonBase
            onClick={handleToggle}
            disabled={disabled}
            aria-label={`Open ${askReveLabel} menu`}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 32,
              borderLeft: "1px solid",
              borderColor: alpha(theme.palette.common.white, 0.25),
              "&:active": {
                backgroundColor: alpha(theme.palette.common.black, 0.1),
              },
              "&.Mui-disabled": { opacity: 0.5 },
            }}
          >
            <Box
              component="span"
              sx={{
                display: "flex",
                transform: open ? "rotate(180deg)" : "none",
                transition: "transform 0.15s ease",
              }}
            >
              <ChevronDownIcon />
            </Box>
          </ButtonBase>
        </Box>
      </Box>
    </ClickAwayListener>
  );
});

export default AskReveActionMenu;
